import { type FormEvent, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import { LanguageSwitch } from '@/components/language-switch';
import { Splash } from '@/components/splash';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api/client';
import { isBusyRetryable, runWithBusyRetry } from '@/lib/api/busy-retry';
import { isApiError } from '@/lib/api/errors';
import { useBusyRun } from '@/lib/api/use-busy-run';
import { useAuth } from '@/lib/auth/auth-context';
import { takeReturnTo } from '@/lib/auth/return-to';
import { warnLogoutStillPending } from '@/lib/auth/logout-pending';
import { settlePendingLogout } from '@/lib/auth/strategy';
import { setAccessToken } from '@/lib/auth/token-store';

type LoginApiResult =
  | { accessToken: string }
  | { mfaRequired: true; mfaToken: string }
  | { mfaEnrollmentRequired: true; mfaToken: string };

/** Seeded principals from `apps/api/prisma/seed-core.ts` — click to fill the form. */
export const DEMO_ACCOUNTS = [
  { email: 'sysadmin@awards.dev', password: 'SysAdmin123!', labelKey: 'auth.demo.superAdmin' /* System admin */ },
  { email: 'tenantadmin@awards.dev', password: 'TenantAdmin123!', labelKey: 'auth.demo.tenantAdmin' /* Organisation admin */ },
  { email: 'applicant@awards.dev', password: 'Applicant123!', labelKey: 'auth.demo.applicant' /* Applicant */ },
  { email: 'evaluator1@awards.dev', password: 'Evaluator123!', labelKey: 'auth.demo.evaluator' /* Evaluator */ },
] as const;

/**
 * The sign-in page.
 *
 * Email/password is the primary path (LOCAL mode, and the way testers use demo
 * accounts). SSO is a secondary option regardless of platform auth mode.
 *
 * In OIDC mode this is also the loop-breaker: ProtectedRoute lands here when
 * the IdP sent the user back still unauthenticated, instead of redirecting
 * forever.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { status, acceptSession, loginWithSso } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  // A flow that ends in an inline error hands focus back to its submit button
  // once the form is re-enabled (it is disabled, so unfocusable, while busy).
  const run = useBusyRun(() => submitRef.current?.focus());
  const busy = run.phase !== 'idle';
  // R8: from the first busy answer of a password sign-in until it ends.
  const retrying = run.retry !== null;

  if (status === 'unknown') return <Splash />;
  if (status === 'authenticated') {
    return <Navigate to={takeReturnTo() ?? '/'} replace />;
  }

  const finish = async (accessToken: string) => {
    await acceptSession(accessToken);
  };

  const onPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const { signal, onAttempt, onRetry } = run.begin();
    let refocus = false;
    try {
      // #119 E2E D1: a sign-out the server never confirmed is ended before a
      // new session replaces its cookie. When it still cannot be, the sign-in
      // goes ahead (never locked out): the new cookie replaces the old one,
      // and the accepted session lifts the mark.
      if (!(await settlePendingLogout())) warnLogoutStillPending('password');
      // R8: a busy answer (503 *_BUSY / AUDIT_CONTENTION) is retried quietly
      // within the shared budget; anything else surfaces at once.
      const result = await runWithBusyRetry(
        () => api.post<LoginApiResult>('/auth/login', { email: email.trim(), password }, { noRetry: true, signal }),
        { signal, onAttempt, onRetry },
      );
      if ('accessToken' in result && result.accessToken) {
        await finish(result.accessToken);
        return;
      }
      if ('mfaEnrollmentRequired' in result && result.mfaEnrollmentRequired) {
        setError(/* This account needs a second step to sign in. Ask an administrator to help you set it up. */ t('auth.errors.mfaEnrollment'));
        return;
      }
      if ('mfaRequired' in result && result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaCode('');
        return;
      }
      setError(/* Sign-in failed. Please try again. */ t('auth.errors.generic'));
    } catch (err) {
      // Unmounted (or superseded) mid-flight: no error UI.
      if (signal.aborted) return;
      setError(messageForLoginError(err, t));
      refocus = true;
    } finally {
      if (!signal.aborted) run.settle(refocus);
    }
  };

  const onMfaSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    // The run is only the busy state here: one request, never retried, never aborted.
    run.begin();
    let refocus = false;
    try {
      setAccessToken(mfaToken);
      const result = await api.post<{ accessToken: string }>(
        '/auth/2fa/verify',
        { code: mfaCode.trim() },
        { noRetry: true },
      );
      await finish(result.accessToken);
    } catch (err) {
      // R8: no automatic retry here. A busy answer shows `auth.errors.busy`
      // after this single request and leaves the retry to the person.
      setAccessToken(null);
      setError(messageForLoginError(err, t));
      refocus = true;
    } finally {
      run.settle(refocus);
    }
  };

  return (
    <div className="grid min-h-dvh place-items-center p-6" style={{ background: 'linear-gradient(160deg, #2A3470 0%, #3E4E9D 45%, #5566BB 100%)' }}>
      <div className="w-full max-w-[420px] rounded-[20px] border bg-card px-8 py-9 shadow-[var(--shadow-lg)]">
        <div className="mb-8 flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-xl bg-primary font-display text-lg font-bold text-white">I</span>
          <div className="leading-tight">
            <p className="text-[18px] font-bold text-secondary-foreground">
              {/* AWARDS */ t('app.name')} <span className="font-medium text-muted-foreground">· {/* Ingazi */ t('app.org')}</span>
            </p>
            <p className="text-xs text-muted-foreground">{/* Manage awards and competitions */ t('app.tagline')}</p>
          </div>
        </div>

        {mfaToken ? (
          <>
            <h1 className="mb-2 text-[22px] font-bold text-secondary-foreground">{/* Verification code */ t('auth.mfaTitle')}</h1>
            <p className="mb-6 text-sm text-muted-foreground">{/* Enter the code from your authenticator app. */ t('auth.mfaBlurb')}</p>
            <form onSubmit={onMfaSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="mfa-code">{/* Code */ t('auth.mfaCode')}</Label>
                <Input
                  id="mfa-code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={mfaCode}
                  onChange={(ev) => setMfaCode(ev.target.value)}
                  required
                  disabled={busy}
                />
              </div>
              {error ? (
                <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <Button ref={submitRef} type="submit" size="lg" className="w-full" disabled={busy}>
                {busy ? /* Verifying… */ t('auth.mfaVerifying') : /* Verify */ t('auth.mfaVerify')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                disabled={busy}
                onClick={() => {
                  setMfaToken(null);
                  setMfaCode('');
                  setError(null);
                }}
              >
                {/* Back to password */ t('auth.mfaBack')}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mb-2 text-[22px] font-bold text-secondary-foreground">{/* Sign in */ t('auth.signIn')}</h1>
            <p className="mb-6 text-sm text-muted-foreground">{/* Enter your email and password. */ t('auth.blurb')}</p>
            <form onSubmit={onPasswordSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{/* Email */ t('auth.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  required
                  disabled={busy}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">{/* Password */ t('auth.password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  required
                  disabled={busy}
                />
              </div>
              {error ? (
                <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <Button ref={submitRef} type="submit" size="lg" className="w-full" disabled={busy}>
                {retrying ? (
                  <>
                    <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
                    {/* Busy — retrying… */ t('auth.busyRetrying')}
                  </>
                ) : busy ? (
                  /* Signing in… */ t('auth.submitting')
                ) : (
                  /* Sign in */ t('auth.signIn')
                )}
              </Button>
              {/* Persistent (empty when idle) so screen readers announce the wait (R8). */}
              <p aria-live="polite" className="text-center text-xs text-muted-foreground empty:!mt-0">
                {retrying ? /* Lots of people are signing in. We’ll keep trying for a few seconds. */ t('auth.busyStatus') : null}
              </p>
            </form>

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{/* or */ t('auth.or')}</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Button type="button" variant="outline" size="lg" className="w-full" disabled={busy} onClick={() => loginWithSso('STAFF')}>
              {/* Sign in with your organisation */ t('auth.signInWithIdp')}
            </Button>

            <div className="mt-6 rounded-xl bg-muted p-3.5">
              <p className="mb-2 text-xs font-semibold text-foreground">{/* Demo accounts */ t('auth.demoTitle')}</p>
              <ul className="flex flex-col gap-0.5">
                {DEMO_ACCOUNTS.map((a) => (
                  <li key={a.email}>
                    <button
                      type="button"
                      onClick={() => {
                        setEmail(a.email);
                        setPassword(a.password);
                      }}
                      className="w-full rounded-md px-0 py-1 text-left text-xs font-medium text-ingazi-orange hover:underline"
                    >
                      {t(a.labelKey)}: {a.email}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        <p className="mt-5 text-center text-sm text-muted-foreground">
          <Link to="/opportunities" className="font-medium text-primary hover:underline">
            {/* See open awards → */ t('catalog.browse')}
          </Link>
        </p>

        <div className="mt-6 flex justify-center">
          <LanguageSwitch />
        </div>
      </div>
    </div>
  );
}

function messageForLoginError(err: unknown, t: (key: string) => string): string {
  if (!isApiError(err)) return /* Sign-in failed. Please try again. */ t('auth.errors.generic');
  const map: Record<string, string> = {
    BAD_CREDENTIALS: /* Incorrect email or password. */ t('auth.errors.badCredentials'),
    ACCOUNT_LOCKED: /* This account is locked. */ t('auth.errors.locked'),
    LOGIN_DISABLED_OIDC: /* Password sign-in is turned off. Sign in with your organisation, or ask an administrator… */ t('auth.errors.oidcDisabled'),
    VALIDATION_FAILED: /* Please check your details and try again. */ t('auth.errors.validation'),
    // R8: a throttled or failed sign-in never shows the raw server text.
    RATE_LIMITED: /* Sign-in failed. Please try again. */ t('auth.errors.generic'),
    INTERNAL_ERROR: /* Sign-in failed. Please try again. */ t('auth.errors.generic'),
  };
  if (isBusyRetryable(err)) return /* The service is very busy right now. Please try again in a moment. */ t('auth.errors.busy');
  return map[err.code] ?? (err.message || /* Sign-in failed. Please try again. */ t('auth.errors.generic'));
}
